import { Money as ConnectMoney } from '@square/dashboard-protos/models/squareup/connect/v2/common/money';
import { Money as CommonMoney } from '@square/dashboard-protos/models/squareup/common/currency';
import { CurrencyCode } from '@square/dashboard-protos/models/squareup/common/currency_codes';
import { assertNonNullable } from '@banking/ui/utils/assert';
import { SATOSHI_ABBREVIATION_THRESHOLD, BITCOIN_TO_SATOSHI } from '@banking/ui/utils/constants';
import { formatSatoshis, formatBitcoinAmount } from '@banking/ui/utils/format-bitcoin';

// NOTE(steckel): This is a caching mechanism so that subsequent calls for a
// currency code's formatter utilize a shared-instance rather than requiring
// new ones for each call.
const NUMBER_FORMATTER_CACHE = new Map();

/**
 * Function for formatting Money protos.
 *
 * This function exists to support the squareup.connect.v2.common.money.Money
 * proto alongside the squareup.common.currency.Money proto already
 * supported within the Dashboard-wide Formatter-Service.
 *
 * It also reconfigures the LGTM NumberFormatter (which is exposed by
 * FormatterService.buildCurrencyFormatter) to format negative money with a
 * negative sign prefix rather than the accounting standard which wraps
 * numbers in parentheses (e.g. "-$5.00" vs. "($5.00)").
 *
 * For historical API reasons, the function also maps includeMinor boolean
 * argument to the maximumFractionDigits and minimumFractionDigits
 * configuration settings.
 */
export default function (money, formatterService, includeMinor = true, intl, formatAsCents = false) {
  const currencyCode = assertNonNullable(money instanceof CommonMoney ? CurrencyCode[money.currencyCode] : money instanceof ConnectMoney ? CurrencyCode[money.currency] : null);
  let amount = money.amount;
  const shouldUseAbbreviation = currencyCode === 'BTC' && amount >= SATOSHI_ABBREVIATION_THRESHOLD && formatAsCents;
  if (currencyCode === 'BTC') {
    amount = formatBitcoinAmount({
      amount: money.amount,
      currencyCode,
      shouldUseAbbreviation,
      formatAsCents
    });
  }
  const singular = amount === 1;
  const fullBTC = amount < BITCOIN_TO_SATOSHI;
  const key = `${currencyCode}-${includeMinor}-${singular}-${formatAsCents}-${shouldUseAbbreviation}-${fullBTC}`;
  let formatter = NUMBER_FORMATTER_CACHE.get(key);
  if (formatter == null) {
    if (formatAsCents) {
      formatter = formatterService.getCurrencyCentFormatter(currencyCode);
    } else {
      formatter = formatterService.buildCurrencyFormatter(currencyCode);
    }
    formatter.setMaximumFractionDigits(includeMinor ? formatter.maximumFractionDigits() : 0).setMinimumFractionDigits(includeMinor ? formatter.minimumFractionDigits() : 0).setNegativePrefix(`-${formatter.positivePrefix()}`).setNegativeSuffix('');
    if (currencyCode === 'BTC' && intl && formatAsCents) {
      formatSatoshis(formatter, intl, amount, shouldUseAbbreviation);
    }
    NUMBER_FORMATTER_CACHE.set(key, formatter);
  }
  return formatter.format(amount);
}